import React, { useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { BrowserRouter, StaticRouter } from 'react-router-dom';
import { ApolloProvider } from '~/lazy_apollo/client';
import { writeQueries } from '~/utils/apollo';
import loadApolloErrorMessages from '~/utils/loadApolloErrorMessages';
import { setApolloClient } from '../../../utils/apolloClient';
import { isSSR } from '../../../utils/dom';
import { initApollo } from '../../../utils/initApollo';
import { DefaultPopmenuConfig, PopmenuConfigContext } from '../../../utils/withPopmenuConfig';
import { ThemeProvider } from '../../../utils/withStyles';
import WindowSizeProvider from '../../../shared/WindowSizeProvider';
import { type PopmenuConfigType, type PreloadedQuery, type EatAppProps } from '../../types/EatAppProps';

type ProvidersProps = EatAppProps & {
  children: React.ReactNode
}

type ApolloProps = {
  children: React.ReactNode
  operationStoreEnabled: boolean
  preloadQueries: PreloadedQuery[]
}

type PopmenuConfigProps = {
  children: React.ReactNode
  popmenuConfig: PopmenuConfigType
}

type RouterProps = {
  children: React.ReactNode
  requestPath: string
}

const Apollo = ({ children, operationStoreEnabled, preloadQueries }: ApolloProps) => {
  const client = initApollo({
    operationStoreEnabled,
    ssr: isSSR,
    url: '/graphql',
  });
  loadApolloErrorMessages();
  writeQueries(preloadQueries, client);
  setApolloClient(client);
  return (
    <ApolloProvider client={client}>
      {children}
    </ApolloProvider>
  );
};

const PopmenuConfig = ({ children, popmenuConfig }: PopmenuConfigProps) => {
  const value = useMemo(() => ({
    ...DefaultPopmenuConfig,
    ...popmenuConfig,
  }), [popmenuConfig]);
  return (
    <PopmenuConfigContext.Provider value={value}>
      {children}
    </PopmenuConfigContext.Provider>
  );
};

const Router = ({ children, requestPath }: RouterProps) => {
  if (isSSR) {
    return (
      <StaticRouter location={requestPath} context={{}}>
        {children}
      </StaticRouter>
    );
  }
  return (
    <BrowserRouter>
      {children}
    </BrowserRouter>
  );
};

export const Providers = ({ children, operationStoreEnabled, popmenuConfig, preloadQueries, requestPath }: ProvidersProps) => (
  <Apollo operationStoreEnabled={operationStoreEnabled} preloadQueries={preloadQueries}>
    <IntlProvider locale="en">
      <ThemeProvider>
        <PopmenuConfig popmenuConfig={popmenuConfig}>
          <Router requestPath={requestPath}>
            <WindowSizeProvider desktop tablet={false} mobile={false}>
              {children}
            </WindowSizeProvider>
          </Router>
        </PopmenuConfig>
      </ThemeProvider>
    </IntlProvider>
  </Apollo>
);
